import { useDispatch, useSelector } from "react-redux";
import { changeRightPanelTab, setHasUnsavedChanges, setRightPanelActiveTab } from "../../../store/appSlice";
import { RightPanelTabs } from "../../../constants/Panels";

import CloseIcon from "../../../assets/close.svg?react";
import ReturnIcon from "../../../assets/arrow-back.svg?react";

import "./SeaCableEditor.css";
import { useContext, useEffect, useRef, useState } from "react";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import {
    applyEdits,
    findAndSelectFeature,
    getFeatureFromGlobalId,
    getMapView,
    highlightFeature,
} from "../../../utils/arcgisUtils";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import {
    createSeaDepthElevationLayer,
    createSeaDepthImageryLayer,
    getAggregatingLineLengths,
    getAggregatingSegmentLengths,
    getPointHeights,
    queryDepthElevation,
} from "../../../utils/seacableUtils";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import { AppContext } from "../../../Context";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator.js";

import TrashIcon from "../../../assets/trash-can.svg?react";
import Plot from "../../genericComponents/Plot/Plot";
import { Data, Layout } from "plotly.js";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import Point from "@arcgis/core/geometry/Point";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { StoreState } from "../../../store/rootReducer";
import { NFSStatus } from "../../../utils/nfsUtils";
import { AppDispatch } from "../../../store/storeSetup";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface SeacableEditorProps {
    readonly: boolean;
}
const SeaCableEditor = (props: SeacableEditorProps) => {
    const dispatch = useDispatch<AppDispatch>();

    const appContext = useContext(AppContext);
    const seacableStartFeature = appContext?.seacableStartFeature;
    const seacableEndFeature = appContext?.seacableEndFeature;
    const selectedFeature = appContext?.selectedFeature.value;
    const shownFeature = appContext?.shownFeature.value;
    // Another feature (e.g. an overett) can stay selected in the editor while a sea cable is opened from the viewer
    const isSeaCableSelected = selectedFeature?.layer?.id === "Sjøkabel";

    const seacableStartRef = useRef<Graphic | null>(null);
    const seacableEndRef = useRef<Graphic | null>(null);

    const highlightRef = useRef<ResourceHandle | null>(null);

    const [layout, setLayout] = useState<Partial<Layout>>({});
    const [data, setData] = useState<Data[]>([]);
    const [isDrawing, setIsDrawing] = useState<boolean>(false);

    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);
    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const depthLayerRef = useRef<ElevationLayer>(null);
    const imageryDepthLayerRef = useRef<ImageryLayer>(null);

    const seacableLineSymbol = useRef<SimpleLineSymbol>(
        new SimpleLineSymbol({
            color: [255, 0, 0, 1],
            width: 2,
            style: "solid",
        })
    );

    const createObjectVMRef = useRef<SketchViewModel | null>(null);

    const [cableGraphic, setCableGraphic] = useState<Graphic | null>(null);
    const [actualCableLength, setActualCableLength] = useState<number | null>(null);
    const [cableLength, setCableLength] = useState<number | null>(null);

    const closePanel = (goToEditor: boolean, triggerWarning: boolean = true) => {
        if (hasUnsavedChanges && triggerWarning) {
            if (
                !globalThis.confirm(
                    "Du har ulagrede endringer i sjøkabelverktøyet. Vil du fortsette, dette vil forkaste endringene?"
                )
            ) {
                return;
            }
        }
        dispatch(setHasUnsavedChanges(false));
        clearCableGraphic();
        seacableStartFeature?.set(null);
        seacableEndFeature?.set(null);

        if (goToEditor) {
            dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
        } else {
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
        }
    };

    const startDrawPolyline = () => {
        const mapView = getMapView();
        if (!mapView) return;
        dispatch(setHasUnsavedChanges(true));
        let sketchVM = createObjectVMRef.current;
        let sketchLayer = mapView?.map?.findLayerById("seacableSketchLayer") as GraphicsLayer;
        if (!sketchLayer) {
            sketchLayer ??= new GraphicsLayer({ id: "seacableSketchLayer", listMode: "hide" });
            mapView?.map?.add(sketchLayer);
        }
        sketchVM ??= new SketchViewModel({
            creationMode: "update",
            defaultUpdateOptions: {
                tool: "reshape",

                toggleToolOnClick: true,
            },
            snappingOptions: {
                enabled: false,
            },
            updateOnGraphicClick: true,
            polylineSymbol: seacableLineSymbol.current,
            layer: sketchLayer,
            view: mapView,
        });
        if (createObjectVMRef.current) {
            clearCableGraphic();
        }

        createObjectVMRef.current = sketchVM;

        setIsDrawing(true);
        if (selectedFeature?.layer?.id === "Sjøkabel") {
            sketchLayer.removeAll();
            const clonedGraphic = selectedFeature.clone();
            clonedGraphic.symbol = seacableLineSymbol.current;
            sketchLayer.add(clonedGraphic);
            createObjectVMRef.current.update(clonedGraphic);
        } else {
            createObjectVMRef.current.create("polyline");
        }

        createObjectVMRef.current.on("create", (event) => {
            if (event.state === "complete" && event.graphic) {
                // Force state update with a new object reference
                let graphicPaths = (event.graphic.clone().geometry as Polyline).paths;

                if (seacableStartRef.current) {
                    // add start Point to front of paths array
                    const startPoint = [
                        (seacableStartRef.current.geometry as Point).x,
                        (seacableStartRef.current.geometry as Point).y,
                    ];
                    graphicPaths[0].unshift(startPoint);
                }
                if (seacableEndRef.current) {
                    // add end Point to back of paths array
                    const endPoint = [
                        (seacableEndRef.current.geometry as Point).x,
                        (seacableEndRef.current.geometry as Point).y,
                    ];
                    graphicPaths[0].push(endPoint);
                }

                const newGraphic = new Graphic({
                    geometry: new Polyline({
                        paths: graphicPaths,
                        spatialReference: (event.graphic.geometry as Polyline).spatialReference,
                    }),
                    symbol: event.graphic.symbol,
                });

                setCableGraphic(newGraphic);
            }
        });

        createObjectVMRef.current.on("update", (event) => {
            if (
                event.toolEventInfo &&
                ((event.toolEventInfo?.type as string).includes("stop") ||
                    (event.toolEventInfo?.type as string).includes("complete") ||
                    (event.toolEventInfo?.type as string).includes("remove"))
            ) {
                // Force state update with a new object reference
                let graphicPaths = (event.graphics[0].clone().geometry as Polyline).paths;

                if (seacableStartRef.current) {
                    // add start Point to front of paths array
                    const startPoint = [
                        (seacableStartRef.current.geometry as Point).x,
                        (seacableStartRef.current.geometry as Point).y,
                    ];
                    graphicPaths[0].unshift(startPoint);
                }
                if (seacableEndRef.current) {
                    // add end Point to back of paths array
                    const endPoint = [
                        (seacableEndRef.current.geometry as Point).x,
                        (seacableEndRef.current.geometry as Point).y,
                    ];
                    graphicPaths[0].push(endPoint);
                }

                const newGraphic = new Graphic({
                    geometry: new Polyline({
                        paths: graphicPaths,
                        spatialReference: (event.graphics[0].geometry as Polyline).spatialReference,
                    }),

                    symbol: event.graphics[0].symbol,
                });

                setCableGraphic(newGraphic);
            }
        });
    };

    const calculateCableLength = async (graphic?: Graphic) => {
        if (!cableGraphic && !graphic) return;

        const tempGraphic = graphic ?? cableGraphic;
        if (!(tempGraphic?.geometry instanceof Polyline)) return;

        const polyline = tempGraphic.geometry;
        const { actualLength, detailedGeometry } = await queryDepthElevation(
            polyline,
            depthLayerRef.current ?? undefined
        );
        setActualCableLength(actualLength);
        const length = lengthOperator.execute(polyline, { unit: "meters" });
        setCableLength(length);
        const mapview = getMapView();
        if (mapview?.map) {
            const editLayer = mapview.map.findLayerById("seacableEditorLayer") as GraphicsLayer;
            if (editLayer) {
                editLayer.removeAll();
                editLayer.add(tempGraphic.clone());
            }
        }
        drawPlot(detailedGeometry as Polyline);
    };

    const saveSeaCable = async () => {
        if (!cableGraphic && !selectedFeature) return;

        const mapview = getMapView();
        const seaCableLayer = mapview?.map?.findLayerById("Sjøkabel") as FeatureLayer;
        if (!seaCableLayer) return;

        if (selectedFeature?.layer?.id === "Sjøkabel") {
            const updatedGraphic = selectedFeature.clone();
            if (cableGraphic?.geometry) {
                updatedGraphic.geometry = cableGraphic.geometry;
            }
            updatedGraphic.attributes = { ...selectedFeature.attributes };
            updatedGraphic.attributes = {
                ...updatedGraphic.attributes,

                // latitude: (updatedGraphic.geometry as Point)?.latitude?.toFixed(8),
                // longitude: (updatedGraphic.geometry as Point)?.longitude?.toFixed(8),
            };
            if (seacableStartFeature?.value) {
                updatedGraphic.attributes["fra_elektroskap"] = seacableStartFeature.value.attributes.globalid;
            } else {
                updatedGraphic.attributes["fra_elektroskap"] = null;
            }
            if (seacableEndFeature?.value) {
                if (seacableEndFeature.value?.layer?.id === "Elektroskap") {
                    updatedGraphic.attributes["til_elektroskap"] = seacableEndFeature.value.attributes.globalid;
                    updatedGraphic.attributes["til_lys"] = null;
                } else {
                    updatedGraphic.attributes["til_lys"] = seacableEndFeature.value.attributes.globalid;
                    updatedGraphic.attributes["til_elektroskap"] = null;
                }
            } else {
                updatedGraphic.attributes["til_elektroskap"] = null;
                updatedGraphic.attributes["til_lys"] = null;
            }

            await applyEdits(seaCableLayer, { updateFeatures: [updatedGraphic] });
            findAndSelectFeature(seaCableLayer, updatedGraphic, (graphic) => {
                appContext?.selectedFeature.set(graphic);
            });
        } else if (cableGraphic) {
            cableGraphic.attributes = {
                nfs_status: NFSStatus.PROJECTED,
            };
            if (seacableStartFeature?.value) {
                cableGraphic.attributes["fra_elektroskap"] = seacableStartFeature.value.attributes.globalid;
            }
            if (seacableEndFeature?.value) {
                if (seacableEndFeature.value?.layer?.id === "Elektroskap") {
                    cableGraphic.attributes["til_elektroskap"] = seacableEndFeature.value.attributes.globalid;
                } else {
                    cableGraphic.attributes["til_lys"] = seacableEndFeature.value.attributes.globalid;
                }
            }

            const result = await applyEdits(seaCableLayer, { addFeatures: [cableGraphic] });
            const objectId = result.addFeatureResults[0].objectId;
            cableGraphic.attributes["objectid"] = objectId;
            findAndSelectFeature(seaCableLayer, cableGraphic, (graphic) => {
                appContext?.selectedFeature.set(graphic);
            });
        }
        dispatch(setHasUnsavedChanges(false));
        closePanel(true, false);
    };

    const clearCableGraphic = () => {
        const mapview = getMapView();
        mapview?.graphics.removeAll();

        // MapNotes are not in use therefore we infer that the layer is GraphicsLayer
        (createObjectVMRef.current?.layer as GraphicsLayer)?.removeAll();
        createObjectVMRef.current?.cancel();
        const sketchLayer = mapview?.map?.findLayerById("seacableSketchLayer") as GraphicsLayer;
        sketchLayer?.removeAll();
        const graphicsLayer = mapview?.map?.findLayerById("seacableEditorLayer") as GraphicsLayer;
        graphicsLayer?.removeAll();
        setCableGraphic(null);
        setActualCableLength(null);
        setCableLength(null);
        setIsDrawing(false);
        setData([]);
    };

    const drawPlot = (lineWithZ: Polyline) => {
        const tempLayout: Partial<Layout> = {};
        tempLayout.height = 300;
        tempLayout.width = 350;
        tempLayout.showlegend = false;

        tempLayout.margin = {
            b: 20,
            t: 20,
            l: 20,
            r: 20,
        };
        const tempData: Data[] = [];

        // Actual length of sea cable
        const aggregatedLengths = getAggregatingSegmentLengths(lineWithZ);
        // Length of line 2D
        const aggregatedLineLengths = getAggregatingLineLengths(lineWithZ);

        const pointHeights = getPointHeights(lineWithZ);
        const cableData: Data = {
            title: {
                text: "Sjøkabel",
            },
            name: "Sjøkabel",
            x: aggregatedLengths,
            y: pointHeights,
            marker: { opacity: 0 },
            line: { width: 4, color: "black", smoothing: 0.3 },
            visible: false,
        };
        const groundData: Data = {
            title: {
                text: "Terreng",
            },

            name: "Terreng",
            x: aggregatedLengths,
            y: pointHeights,
            fill: "tonexty",
            fillcolor: "grey",
            marker: { opacity: 0 },
            hoverinfo: "none",
            line: { width: 0, color: "black" },
            visible: false,
        };

        const minHeight = Math.min(...pointHeights);
        const minY: number[] = new Array(aggregatedLengths.length);
        minY.fill(minHeight, 0, aggregatedLengths.length);
        const waterFill: Data = {
            x: aggregatedLengths,
            y: minY,
            name: "Vann",
            fill: "tozeroy",
            fillcolor: "lightblue",
            marker: { opacity: 0 },
            line: { width: 0 },
            hoverinfo: "none",
            visible: false,
        };

        const cableDataProfile: Data = {
            title: {
                text: "Sjøkabel",
            },
            name: "Sjøkabel",
            x: aggregatedLineLengths,
            y: pointHeights,
            marker: { opacity: 0 },
            line: { width: 4, color: "black", smoothing: 0.3 },
            visible: true,
        };
        const groundDataProfile: Data = {
            title: {
                text: "Terreng",
            },
            name: "Terreng",
            x: aggregatedLineLengths,
            y: pointHeights,
            fill: "tonexty",
            fillcolor: "grey",
            marker: { opacity: 0 },
            line: { width: 0, color: "black" },
            hoverinfo: "none",
            visible: true,
        };

        const minYProfile: number[] = new Array(aggregatedLineLengths.length);
        minYProfile.fill(minHeight, 0, aggregatedLineLengths.length);
        const waterFillProfile: Data = {
            x: aggregatedLineLengths,
            y: minYProfile,
            name: "Vann",
            fill: "tozeroy",
            fillcolor: "lightblue",
            marker: { opacity: 0 },
            line: { width: 0 },
            hoverinfo: "none",
            visible: true,
        };

        tempData.push(waterFill);
        tempData.push(groundData);
        tempData.push(cableData);
        tempData.push(waterFillProfile);
        tempData.push(groundDataProfile);
        tempData.push(cableDataProfile);

        setData(tempData);
        setLayout(tempLayout);
    };

    const highlight = async (feature: Graphic | null) => {
        if (highlightRef.current) {
            highlightRef.current.remove();
        }
        if (feature) {
            highlightRef.current = (await highlightFeature(feature)) ?? null;
        }
    };

    const clearHighlight = () => {
        if (highlightRef.current) {
            highlightRef.current.remove();
        }
    };

    const hasEndpointsChanged = () => {
        if (seacableStartFeature?.value?.attributes.globalid !== selectedFeature?.attributes.fra_elektroskap) {
            if (!hasUnsavedChanges) {
                dispatch(setHasUnsavedChanges(true));
            }
            return true;
        }
        if (
            seacableEndFeature?.value?.attributes.globalid !== selectedFeature?.attributes.til_elektroskap &&
            seacableEndFeature?.value?.attributes.globalid !== selectedFeature?.attributes.til_lys
        ) {
            if (!hasUnsavedChanges) {
                dispatch(setHasUnsavedChanges(true));
            }
            return true;
        }
        return false;
    };

    const findAndSetStartAndEndObjects = () => {
        if (selectedFeature?.attributes["fra_elektroskap"]) {
            const mapview = getMapView();
            const elektroskapLayer = mapview?.map?.findLayerById("Elektroskap") as FeatureLayer;
            if (elektroskapLayer) {
                const globalId = selectedFeature.attributes["fra_elektroskap"];
                getFeatureFromGlobalId(elektroskapLayer.layerId as number, globalId).then((feature) => {
                    if (feature) {
                        seacableStartFeature?.set(feature);
                    }
                });
            }
        }
        if (selectedFeature?.attributes["til_elektroskap"]) {
            const mapview = getMapView();
            const elektroskapLayer = mapview?.map?.findLayerById("Elektroskap") as FeatureLayer;
            if (elektroskapLayer) {
                const globalId = selectedFeature.attributes["til_elektroskap"];
                getFeatureFromGlobalId(elektroskapLayer.layerId as number, globalId).then((feature) => {
                    if (feature) {
                        seacableEndFeature?.set(feature);
                    }
                });
            }
        }
        if (selectedFeature?.attributes["til_lys"]) {
            const mapview = getMapView();
            const lysLayer = mapview?.map?.findLayerById("Lys") as FeatureLayer;
            if (lysLayer) {
                const globalId = selectedFeature.attributes["til_lys"];
                getFeatureFromGlobalId(lysLayer.layerId as number, globalId).then((feature) => {
                    if (feature) {
                        seacableEndFeature?.set(feature);
                    }
                });
            }
        }
        dispatch(setHasUnsavedChanges(false));
    };

    const goToEditing = () => {
        dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
    };

    useEffect(() => {
        if (selectedFeature?.layer?.id === "Sjøkabel") {
            findAndSetStartAndEndObjects();
        }
        if (shownFeature && !isSeaCableSelected && rightPanelActiveTab === RightPanelTabs.SEA_CABLE) {
            calculateCableLength(shownFeature);
        }
    }, [selectedFeature, shownFeature, rightPanelActiveTab]);

    useEffect(() => {
        seacableStartRef.current = seacableStartFeature?.value ?? null;
    }, [seacableStartFeature?.value]);

    useEffect(() => {
        seacableEndRef.current = seacableEndFeature?.value ?? null;
    }, [seacableEndFeature?.value]);

    useEffect(() => {
        if (cableGraphic) {
            calculateCableLength();
        }
    }, [cableGraphic]);

    useEffect(() => {
        if (rightPanelActiveTab === RightPanelTabs.SEA_CABLE) {
            findAndSetStartAndEndObjects();
            if (selectedFeature?.layer?.id === "Sjøkabel") {
                calculateCableLength(selectedFeature);
            }
            const mapview = getMapView();
            const seaCableLayer = mapview?.map?.findLayerById("Sjøkabel");
            const elektroskapLayer = mapview?.map?.findLayerById("Elektroskap");
            if (seaCableLayer) {
                seaCableLayer.visible = true;
            }
            if (elektroskapLayer) {
                elektroskapLayer.visible = true;
            }
        } else {
            clearCableGraphic();
        }
    }, [rightPanelActiveTab]);

    useEffect(() => {
        const loadSeaDepthElevationLayer = async () => {
            const depthLayer = await createSeaDepthElevationLayer();

            await depthLayer.load();
            depthLayerRef.current = depthLayer;
        };

        const loadSeaDepthImageryLayer = async () => {
            const imageryLayer = await createSeaDepthImageryLayer();

            await imageryLayer.load();
            imageryDepthLayerRef.current = imageryLayer;
        };

        loadSeaDepthElevationLayer();
        loadSeaDepthImageryLayer();
    }, []);

    return (
        <div className="sea-cable-editor-container">
            <div className="sea-cable-editor-header">
                {selectedFeature && (
                    <button onClick={goToEditing} className="empty-button flex-row center">
                        <ReturnIcon />
                        Tilbake
                    </button>
                )}
                <h3>Sjøkabelplanlegger</h3>
                <button type="button" className="empty-button" onClick={() => closePanel(false)}>
                    <CloseIcon />
                </button>
            </div>
            <div className="sea-cable-editor-content">
                {!props.readonly && (
                    <>
                        <span>Velg først elektroskapet sjøkabelen begynner i</span>
                        {seacableStartFeature?.value && (
                            <div
                                onMouseEnter={() => highlight(seacableStartFeature?.value)}
                                onMouseLeave={clearHighlight}
                                className="flex-row small-gap space-between"
                            >
                                <span>
                                    Start:{" "}
                                    {seacableStartFeature?.value?.attributes.sjomerke_nr ??
                                        seacableStartFeature?.value?.attributes.objectid}
                                    : {seacableStartFeature?.value?.attributes.navn}
                                </span>
                                <button
                                    className="nfs-button no-flex outline warning"
                                    onClick={() => seacableStartFeature?.set(null)}
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        )}
                        {seacableEndFeature?.value && (
                            <div
                                onMouseEnter={() => highlight(seacableEndFeature?.value)}
                                onMouseLeave={clearHighlight}
                                className="flex-row small-gap space-between"
                            >
                                <span>
                                    Slutt:{" "}
                                    {seacableEndFeature?.value?.attributes.sjomerke_nr ??
                                        seacableEndFeature?.value?.attributes.objectid}
                                    : {seacableEndFeature?.value?.attributes.navn}
                                </span>
                                <button
                                    className="nfs-button no-flex outline warning"
                                    onClick={() => seacableEndFeature?.set(null)}
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        )}
                        <span>Velg deretter elektroskapet eller nautisk objekt sjøkabelen ender i</span>
                        <span>
                            Dersom det er valg startobjekt og/eller sluttobjekt legger vi disse til som start og slutt
                            punkt i tegnet linje.
                        </span>
                    </>
                )}

                <br />
                <span>
                    {cableLength !== null
                        ? `Luftlinje kabellengde: ${cableLength.toFixed(2)} meter`
                        : "Luftlinje kabellengde: Ikke beregnet"}
                </span>
                <span>
                    {actualCableLength !== null
                        ? `Beregnet kabellengde: ${actualCableLength.toFixed(2)} meter`
                        : "Beregnet kabellengde: Ikke beregnet"}
                </span>
                <span>
                    <strong>
                        Lengde er beregnet fra tilgjengelig høyde-/dybdedata og kan avvike fra virkeligheten.
                    </strong>
                </span>
                <br />

                <div className="flex-row small-gap">
                    <NfsButton disabled={isDrawing} onClick={startDrawPolyline}>
                        {selectedFeature?.layer?.id === "Sjøkabel" ? "Oppdater sjøkabel" : "Tegn sjøkabel"}
                    </NfsButton>
                    <NfsButton
                        outlined
                        onClick={() => {
                            clearCableGraphic();
                            findAndSetStartAndEndObjects();
                            if (
                                shownFeature &&
                                !isSeaCableSelected &&
                                rightPanelActiveTab === RightPanelTabs.SEA_CABLE
                            ) {
                                calculateCableLength(shownFeature);
                            }
                        }}
                    >
                        Reset
                    </NfsButton>
                </div>
                {!props.readonly && (
                    <NfsButton disabled={!cableGraphic && !hasEndpointsChanged()} onClick={saveSeaCable}>
                        Lagre og lukk verktøy
                    </NfsButton>
                )}
                {data.length > 0 && (
                    <Plot
                        layout={layout}
                        data={data}
                        config={{ showSendToCloud: false }}
                        className="seacable-chart-plot"
                    />
                )}
            </div>
        </div>
    );
};

export default SeaCableEditor;
